import { Box, Button, Card, Typography } from "@drone-js/core";
import { useStaticDataAtomValue } from "apps/oneOnboarding/staticDataAtom";
import type { FunctionComponent } from "react";
import { useState } from "react";
import { BatchPropertyContent } from "./BatchPropertyContent";
import { useBatchCompletePageViewModel } from "./useBatchCompletePageViewModel";

export const FailedToPublishListings: FunctionComponent = () => {
    const [isExpanded, setIsExpanded] = useState(false);

    const {
        cmsData: {
            complete: {
                propertyCannotBePublishedText,
                propertiesCannotBePublishedText,
                cannotBePublishedDetailText,
                completeAndPublishText,
            },
        },
    } = useStaticDataAtomValue();

    const { failedListings } = useBatchCompletePageViewModel();

    if (failedListings === null || failedListings.length === 0) return null;

    const failedCount = failedListings.length;
    const headerText =
        failedCount === 1
            ? propertyCannotBePublishedText
            : (propertiesCannotBePublishedText?.replace("{0}", failedCount.toString()) ??
              propertiesCannotBePublishedText);

    return (
        <Box mb="16" data-element-name="failed-to-publish-listings" data-testid="batch-failed-listings">
            <Card
                type="non-interactive"
                variant="outlined"
                p="24"
                data-element-name="cannot-be-published-section"
                data-testid="batch-failed-listings-card"
            >
                <Box
                    mb={isExpanded ? "16" : "0"}
                    onClick={() => setIsExpanded(!isExpanded)}
                    cursor="pointer"
                    data-element-name="failed-to-publish-header"
                    data-testid="batch-failed-listings-header"
                >
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                        <Box flexGrow={1}>
                            <Typography
                                variant="heading-default"
                                textColor="feedback.urgent"
                                data-testid="batch-failed-listings-title"
                            >
                                {headerText}
                            </Typography>
                            <Typography
                                variant="body-s"
                                textColor="base.subtle"
                                mt="8"
                                data-testid="batch-failed-listings-description"
                            >
                                {cannotBePublishedDetailText}
                            </Typography>
                        </Box>
                        <Box flexShrink={0} ms="16">
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                style={{
                                    transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                                    transition: "transform 0.2s ease",
                                }}
                                data-testid="batch-failed-listings-toggle-icon"
                            >
                                <path
                                    d="M6 9L12 15L18 9"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </Box>
                    </Box>
                </Box>
                {isExpanded && (
                    <Box data-testid="batch-failed-listings-expanded">
                        {failedListings.map((item, index) => (
                            <Box
                                key={`failed-property-${item.id}`}
                                display="block"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={index < failedListings.length - 1 ? "16" : "0"}
                                qM={{ display: "flex" }}
                                data-testid={`batch-failed-listing-${item.id}`}
                            >
                                <BatchPropertyContent {...item} />
                                <Box
                                    flexShrink={0}
                                    mt="16"
                                    mb="24"
                                    ms="0"
                                    width="100%"
                                    qM={{ mt: "0", mb: "0", ms: "16", width: "auto" }}
                                >
                                    <Button
                                        variant="label"
                                        size="l"
                                        hierarchy="primary"
                                        label={completeAndPublishText}
                                        width="parent-fit"
                                        data-element-name="complete-publish"
                                        data-property-id={item.id}
                                        data-testid={`batch-failed-complete-button-${item.id}`}
                                        onClick={() => {
                                            window.location.href = item.onboardingUrl;
                                        }}
                                    />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
            </Card>
        </Box>
    );
};
